// ADO #584 — where a + button inserts, and whether that place is inside a loop.
//
// Every + button hands VisualFlowBuilder an "add id" that names its insertion
// point (see addNode / findAndAddNode in useFlowStore):
//   <nodeId>                              after a top-level node
//   <decisionId>-branch-<i>[-after-<id>]  inside a Decision / Switch / Try branch
//   <loopId>-loopBody[-after-<id>]        inside a Loop's body
//   <loopId>-afterLast[-after-<id>]       after a Loop finishes (NOT inside it)
//   <id>-after-merge / -after-loop-merge  after a Decision / Loop, same level
//
// Break and Continue only make sense inside a loop body (at any depth, e.g. in
// a Decision inside a Loop), so the + menu offers them only there.

const childLists = (node) => {
  const lists = [];
  if (!node) return lists;
  if (Array.isArray(node.outcomes)) node.outcomes.forEach((o) => lists.push({ nodes: o?.nodes, kind: 'branch' }));
  if (Array.isArray(node.branches)) node.branches.forEach((b) => lists.push({ nodes: b?.nodes, kind: 'branch' }));
  if (Array.isArray(node.loopBody)) lists.push({ nodes: node.loopBody, kind: 'loopBody' });
  if (Array.isArray(node.afterLast)) lists.push({ nodes: node.afterLast, kind: 'afterLast' });
  return lists;
};

// Walk the tree; for each node record whether it sits inside some loop's body.
const indexNodes = (nodes, inLoop, out) => {
  for (const node of nodes || []) {
    if (!node || !node.id) continue;
    out.set(node.id, inLoop);
    childLists(node).forEach(({ nodes: kids, kind }) => {
      indexNodes(kids, kind === 'loopBody' ? true : inLoop, out);
    });
  }
  return out;
};

/**
 * True when a step added at `addId` would sit inside a loop body.
 * `nodes` is the active flow's top-level node list.
 */
export const isInsideLoopAt = (nodes, addId) => {
  if (!addId || typeof addId !== 'string') return false;
  const index = indexNodes(nodes, false, new Map());
  if (index.has(addId)) return index.get(addId);
  // The longest node id that prefixes the add id is the anchor (node ids may
  // themselves contain '-').
  let anchor = null;
  for (const id of index.keys()) {
    if (addId.startsWith(`${id}-`) && (!anchor || id.length > anchor.length)) anchor = id;
  }
  if (!anchor) return false;
  const rest = addId.slice(anchor.length + 1);
  if (rest === 'loopBody' || rest.startsWith('loopBody-')) return true;
  return index.get(anchor) === true;
};

// Node types the + menu never offers as a placeable step.
export const NEVER_PLACEABLE = new Set(['end']);
// Node types offered only inside a loop body.
export const LOOP_ONLY = new Set(['break', 'continue']);

/**
 * The add id that puts a new step right after the step `nodeId`, at whatever
 * depth it sits (a plain id only works at the top level). Null when it is not
 * in `nodes`.
 */
export const addIdAfter = (nodes, nodeId) => {
  const find = (list, prefix) => {
    for (const node of list || []) {
      if (!node || !node.id) continue;
      if (node.id === nodeId) return prefix == null ? nodeId : `${prefix}-after-${nodeId}`;
      const kids = [];
      if (Array.isArray(node.outcomes)) node.outcomes.forEach((o, i) => kids.push([o?.nodes, `${node.id}-branch-${i}`]));
      if (Array.isArray(node.branches)) node.branches.forEach((b, i) => kids.push([b?.nodes, `${node.id}-branch-${i}`]));
      if (Array.isArray(node.loopBody)) kids.push([node.loopBody, `${node.id}-loopBody`]);
      if (Array.isArray(node.afterLast)) kids.push([node.afterLast, `${node.id}-afterLast`]);
      for (const [k, p] of kids) {
        const got = find(k, p);
        if (got) return got;
      }
    }
    return null;
  };
  return find(nodes, null);
};
